import { z } from "zod";
import type { ChannelPlatform } from "./channels.js";
import { tiktokRefusal } from "./tiktok.js";

/**
 * What YouTube asks of every upload, and the one place that says which networks ask per-post questions at all
 * ([32](../../../docs/32-what-each-network-requires.md)).
 *
 * YouTube's API Services policies require the uploader to declare whether a video is made for kids (COPPA), so it is
 * a choice on every post with no default, as TikTok's privacy is. Who can see it is a choice with no default too,
 * since 2026-09-29: it started at Public, so a channel set to "ask me for each video" went out public to anyone who
 * pressed Post without looking, which is the one of the three nobody can take back.
 */

export const YOUTUBE_PRIVACY = ["public", "unlisted", "private"] as const;
export type YouTubePrivacy = (typeof YOUTUBE_PRIVACY)[number];

export const YOUTUBE_PRIVACY_WORDS: Record<YouTubePrivacy, string> = {
  public: "Public",
  unlisted: "Unlisted — only people with the link",
  private: "Private — only you",
};

/**
 * The choices as the worker reads them off a post. Lenient on privacy only for the rows scheduled before it had to be
 * answered, which were public by default then and stay what their owner saw.
 */
export const YouTubeChoices = z.object({
  madeForKids: z.boolean(),
  privacy: z.enum(YOUTUBE_PRIVACY).default("public"),
}).strict();
export type YouTubeChoices = z.infer<typeof YouTubeChoices>;

/** The choices as a post is asked for, scheduled or approved: both answered, neither assumed. */
const YouTubeAnswers = z.object({ madeForKids: z.boolean(), privacy: z.enum(YOUTUBE_PRIVACY) }).strict();

export const YOUTUBE_TEXT = {
  madeForKids: "Is this video made for kids?",
  madeForKidsHint: "YouTube requires you to say, under the law on children's privacy. Made for kids turns off comments and personalised ads on it.",
  chooseKids: "Say whether this video is made for kids — YouTube requires it.",
  choosePrivacy: "Choose who can see it on YouTube.",
} as const;

/** Why these YouTube choices may not be posted, or null. */
export function youtubeRefusal(choices: unknown): string | null {
  if (YouTubeAnswers.safeParse(choices).success) return null;
  const given = (choices ?? {}) as { madeForKids?: unknown; privacy?: unknown };
  return typeof given.madeForKids !== "boolean" ? YOUTUBE_TEXT.chooseKids : YOUTUBE_TEXT.choosePrivacy;
}

/**
 * Why a post's own choices for this network are missing or wrong, or null — for the networks that ask per post
 * (TikTok, YouTube). The API asks it when scheduling and when approving, so no route can let one through unasked.
 */
export function optionsRefusal(platform: ChannelPlatform, options: unknown): string | null {
  if (platform === "tiktok") return tiktokRefusal(options);
  if (platform === "youtube") return youtubeRefusal(options);
  return null;
}
